import { useRouteLoaderData } from "react-router"
import { DocsRouterLink as RouterLink, DocsUiLink as Link } from "~/docs-data/docs-link"
import {
  MapPinIcon,
  VectorSquareIcon,
  SplitIcon,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react"
import { markdownToPlainText } from "~/components/MarkdownBody"
import { CustomBoundariesIcon } from "~/components/CustomBoundariesIcon"
import { canonicalMetricSlugForType, metricCanonicalName } from "~/docs-data/helpers"
import type {
  DocsCatalog,
  DocsLocationType,
} from "~/server/models/docsCatalog.server"
import type { DatasetEntry } from "~/docs-data/datasets"

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  place: MapPinIcon,
  area: VectorSquareIcon,
  way: SplitIcon,
}

// Display-order override for location types. Backend returns alphabetical;
// the docs UX shows place → area → way.
const LT_ORDER: Record<string, number> = { place: 0, area: 1, way: 2 }

type MetricChip = { ltm: string; label: string }

function LocationTypeCard({
  lt,
  metricChips,
}: {
  lt: DocsLocationType
  metricChips: MetricChip[]
}) {
  const Icon = ICON_BY_TYPE[lt.id]
  // Backend description is full markdown; for a card preview we want a
  // single sentence's worth of plain text so it doesn't blow up the layout.
  const preview = lt.description
    ? markdownToPlainText(lt.description).slice(0, 150).trim()
    : ""
  return (
    <RouterLink
      to={`/docs/concepts/location-types/${lt.id}`}
      className="group flex flex-col rounded-xl border bg-card p-6 transition-all hover:border-primary hover:shadow-sm"
    >
      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {Icon && <Icon className="h-6 w-6" />}
      </div>
      <div className="mb-1.5 text-xl font-semibold text-foreground">
        {lt.displayName || lt.id}
      </div>
      {preview && (
        <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
          {preview}
          {lt.description && lt.description.length > preview.length ? "…" : ""}
        </p>
      )}
      <div className="mt-auto border-t pt-4">
        <div className="mb-2 text-[13px] font-semibold text-foreground">
          {metricChips.length} metric{metricChips.length === 1 ? "" : "s"}
        </div>
        {metricChips.length === 0 ? (
          <p className="text-xs italic text-muted-foreground">
            No metrics catalogued yet.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {metricChips.map((c) => (
              <span
                key={c.ltm}
                className="inline-flex rounded-full border bg-muted/40 px-2.5 py-1 text-xs text-muted-foreground"
              >
                {c.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </RouterLink>
  )
}

export default function ConceptsIndex() {
  const docs = useRouteLoaderData("routes/_console.docs") as
    | DocsCatalog
    | undefined
  const datasets: DatasetEntry[] = docs?.datasets ?? []
  const locationTypes = [...(docs?.locationTypes ?? [])].sort((a, b) => {
    const ao = LT_ORDER[a.id] ?? 99
    const bo = LT_ORDER[b.id] ?? 99
    return ao - bo || a.id.localeCompare(b.id)
  })

  // Card chips collapse all variants of a metric concept under one chip
  // (cadence variants AND differently-prefixed LTMs that share the same
  // display concept — e.g. `cbg_foot_traffic_month` + `foot_traffic_month`
  // both become a single "Foot Traffic" chip, and `cbg_trade_area` +
  // `trade_areas_day_al2` collapse into one "Trade Areas" chip). Each chip
  // links to the first member LTM.
  const chipsForType = (locationType: string): MetricChip[] => {
    const seenLTMs = new Set<string>()
    const rows: { ltm: string; name: string }[] = []
    for (const d of datasets) {
      if (d.locationType !== locationType) continue
      if (seenLTMs.has(d.ltm)) continue
      seenLTMs.add(d.ltm)
      rows.push({ ltm: d.ltm, name: d.name })
    }
    const byConcept = new Map<string, typeof rows>()
    const conceptOrder: string[] = []
    for (const r of rows) {
      const concept = metricCanonicalName(r.name)
      if (!byConcept.has(concept)) {
        byConcept.set(concept, [])
        conceptOrder.push(concept)
      }
      byConcept.get(concept)!.push(r)
    }
    conceptOrder.sort((a, b) => a.localeCompare(b))
    return conceptOrder.map((concept) => {
      const members = byConcept.get(concept)!
      // Single-variant concept → link to that LTM directly; multi-variant
      // → typed canonical-overview slug.
      const slug =
        members.length === 1
          ? members[0].ltm
          : canonicalMetricSlugForType(concept, locationType)
      return { ltm: slug, label: concept }
    })
  }

  return (
    <div className="max-w-screen-xl">
      {/* Hero */}
      <section className="mb-12">
        <h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground">
          Concepts
        </h1>
        <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">
          The abstract data model behind Pine59: what is measured, how it is
          built, and how individual privacy is protected. Country-specific
          details live in{" "}
          <Link to="/docs/datasets" variant="inline">Datasets</Link>.
        </p>
      </section>

      {/* Location types */}
      <section className="mb-14">
        <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
          Location types
        </h2>
        <p className="mb-6 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Place, Area, and Way define which metrics are available. Every metric
          is scoped to exactly one location type.
        </p>
        <div className="grid gap-4 md:grid-cols-3">
          {locationTypes.map((lt) => (
            <LocationTypeCard
              key={lt.id}
              lt={lt}
              metricChips={chipsForType(lt.id)}
            />
          ))}
        </div>
        {/* Custom boundaries — not a fourth location type, but the reader
            shouldn't conclude the pre-built location sets are the only
            geography we serve. */}
        <RouterLink
          to="/docs/concepts/location-types/place"
          className="group mt-4 flex items-start gap-3 rounded-lg border border-dashed bg-card p-4 transition-all hover:border-primary hover:shadow-sm"
        >
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <CustomBoundariesIcon className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-semibold text-foreground group-hover:text-primary">
              Custom boundaries
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Have your own map? Place and Area metrics can also be attributed
              to boundaries you define (trade zones, property outlines,
              catchment polygons), so the data lines up with the geography your
              organization already uses.
            </p>
          </div>
        </RouterLink>
      </section>

      {/* Techniques */}
      <section className="mb-14">
        <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
          Techniques
        </h2>
        <p className="mb-6 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          The general techniques behind the data: privacy protections, visit
          detection, and modelling. These articles are un-versioned and
          signal-agnostic;{" "}
          <Link to="/docs/concepts/techniques" variant="inline">
            browse the techniques →
          </Link>{" "}
          Each country&apos;s dataset pages show which apply to that country&apos;s
          signal.{" "}
          <Link to="/docs/datasets" variant="inline">
            Pick a country →
          </Link>
        </p>
      </section>

      {/* Privacy */}
      <section>
        <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
          Privacy
        </h2>
        <p className="mb-6 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          How privacy is built into the data itself: what is never published,
          and the technical mechanisms that enforce it.
        </p>
        <RouterLink
          to="/docs/concepts/techniques/privacy-approach"
          className="group flex max-w-2xl items-start gap-3 rounded-lg border bg-card p-4 transition-all hover:border-primary hover:shadow-sm"
        >
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div>
            <div className="text-sm font-semibold text-foreground group-hover:text-primary">
              Privacy approach
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Pine59 products are built for statistical use only, so no output
              can be linked to an individual person. Which mechanisms apply
              (k5 filtering, differential privacy, …) depends on the country
              and its signal.
            </p>
          </div>
        </RouterLink>
      </section>
    </div>
  )
}
